package fr.frux.core.maps

import androidx.compose.foundation.shape.GenericShape
import androidx.compose.runtime.Composable
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.translate
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.graphics.vector.path
import androidx.compose.ui.graphics.vector.rememberVectorPainter
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin

/** Diametre de la plaque au repos. */
internal val MARKER_BASE_SIZE: Dp = 36.dp

/** Grossissement commun a la selection (plaque, bord, ombre, glyphe). */
internal const val SELECTED_MARKER_SCALE: Float = 1.35f

/**
 * Debordement du bord autour de la plaque. Le bord n'est jamais un trace : c'est l'ecart entre
 * deux disques pleins, seule forme que le pipeline SDF rend sans rognage.
 *
 * Pas d'ombre sur ce chemin (SDK) : le pipeline SDF rend un aplat, jamais un degrade d'alpha, donc
 * une couche d'ombre y devient un anneau gris a bord franc au lieu d'un flou — verifie sur
 * emulateur, cf. rapport shadow-removal. Le bord blanc de 3 dp suffit a decoller la pastille des
 * tuiles claires, sur les quatre cibles : depuis maplibre-compose 0.16 il n'y a plus qu'un chemin de
 * rendu, donc plus de divergence d'ombre a arbitrer.
 */
internal const val MARKER_RIM_SCALE: Float = 1.18f

internal val MARKER_RIM_COLOR: Color = Color.White

/** Ambre « bientot mur » (#204), porte par le bord depuis l'abandon de l'anneau detache. */
internal val MARKER_SOON_RIM_COLOR: Color = Color(0xFFD98E24)

/**
 * Diametre total de la pastille de regroupement, contour compris. Nomme ici plutot que calcule sur
 * place parce que le contour du `CircleLayer` se dessine vers l'exterieur du rayon : le rayon passe
 * au SDK se deduit de ce diametre, il ne le vaut pas.
 */
internal val CLUSTER_SIZE: Dp = 40.dp

/** Epaisseur du contour de la pastille de regroupement. */
internal val CLUSTER_RIM_WIDTH: Dp = 3.dp

/** Respiration entre le sommet du marqueur selectionne et le bas de la bulle. */
private val CALLOUT_GAP: Dp = 8.dp

internal fun markerRimColor(soon: Boolean): Color = if (soon) MARKER_SOON_RIM_COLOR else MARKER_RIM_COLOR

/** Facteur applique a [MARKER_BASE_SIZE]. */
internal fun markerScale(
    selected: Boolean,
    rim: Boolean,
): Float {
    val base = if (rim) MARKER_RIM_SCALE else 1f
    return if (selected) base * SELECTED_MARKER_SCALE else base
}

/**
 * Distance entre le centre du marqueur et le bas de la bulle. Derivee de la geometrie reelle :
 * une valeur en dur reglee sur la plaque au repos laisserait la bulle mordre le marqueur des
 * qu'il grossit.
 */
internal fun calloutBottomOffset(): Dp = MARKER_BASE_SIZE * markerScale(selected = true, rim = true) / 2f + CALLOUT_GAP

private val MarkerDisc: ImageVector by lazy {
    ImageVector
        .Builder(
            name = "MarkerDisc",
            defaultWidth = MARKER_BASE_SIZE,
            defaultHeight = MARKER_BASE_SIZE,
            viewportWidth = 36f,
            viewportHeight = 36f,
        ).apply {
            // Quatre arcs de Bezier : un disque plein, forme la plus sure du pipeline SDF.
            val k = 18f * 0.5523f
            path(fill = SolidColor(Color.Black)) {
                moveTo(36f, 18f)
                curveTo(36f, 18f + k, 18f + k, 36f, 18f, 36f)
                curveTo(18f - k, 36f, 0f, 18f + k, 0f, 18f)
                curveTo(0f, 18f - k, 18f - k, 0f, 18f, 0f)
                curveTo(18f + k, 0f, 36f, 18f - k, 36f, 18f)
                close()
            }
        }.build()
}

/**
 * Nombre de creneaux du bord « a verifier ». Huit : assez pour que la denture se lise comme une
 * propriete du contour et non comme un accident de rendu, assez peu pour qu'un creneau reste
 * visible a 36 dp.
 */
private const val NOTCH_COUNT = 8

/** Part du cycle occupee par la dent ; le reste est le creux. */
private const val NOTCH_FILL_RATIO = 0.6f

/** Segments par arc : l'echantillonnage remplace les Bezier, la denture n'a pas besoin de plus. */
private const val NOTCH_ARC_STEPS = 6

/**
 * Contour crenele, ferme, echantillonne en coordonnees du viewport (centre [center]).
 *
 * Les creux descendent a [innerRadius] et les dents montent a [outerRadius] ; les transitions sont
 * radiales (meme angle, deux rayons), ce qui donne des flancs francs plutot qu'une ondulation.
 * Partage par les deux chemins de rendu pour que la denture ne diverge pas entre eux.
 */
private fun notchedRingPoints(
    outerRadius: Float,
    innerRadius: Float,
    center: Float,
): FloatArray {
    val cycle = 2f * PI.toFloat() / NOTCH_COUNT
    val fill = cycle * NOTCH_FILL_RATIO
    val points = ArrayList<Float>(NOTCH_COUNT * (NOTCH_ARC_STEPS + 1) * 4)

    fun emit(
        angle: Float,
        radius: Float,
    ) {
        points += center + radius * cos(angle)
        points += center + radius * sin(angle)
    }

    repeat(NOTCH_COUNT) { notch ->
        val start = notch * cycle
        repeat(NOTCH_ARC_STEPS + 1) { step ->
            emit(start + fill * step / NOTCH_ARC_STEPS, outerRadius)
        }
        repeat(NOTCH_ARC_STEPS + 1) { step ->
            emit(start + fill + (cycle - fill) * step / NOTCH_ARC_STEPS, innerRadius)
        }
    }
    return points.toFloatArray()
}

/**
 * Disque a bord crenele (#265), dessine a la place de [MarkerDisc] sur la couche de bord quand
 * l'arbre demande verification. Les creux affleurent exactement la plaque (rayon divise par
 * [MARKER_RIM_SCALE], le facteur auquel cette couche est rendue), les dents gardent le debord
 * habituel : la denture se lit sur la silhouette de la plaque coloree, pas sur le blanc seul.
 *
 * Un contour interrompu dit « pas encore ferme » la ou une couleur d'alerte dirait « danger » —
 * registre impose par l'ADR 0015, qui reserve le rouge au sosie mortel.
 */
private val MarkerDiscNotched: ImageVector by lazy {
    val points = notchedRingPoints(outerRadius = 18f, innerRadius = 18f / MARKER_RIM_SCALE, center = 18f)
    ImageVector
        .Builder(
            name = "MarkerDiscNotched",
            defaultWidth = MARKER_BASE_SIZE,
            defaultHeight = MARKER_BASE_SIZE,
            viewportWidth = 36f,
            viewportHeight = 36f,
        ).apply {
            path(fill = SolidColor(Color.Black)) {
                moveTo(points[0], points[1])
                for (i in 2 until points.size step 2) {
                    lineTo(points[i], points[i + 1])
                }
                close()
            }
        }.build()
}

/**
 * Forme crenelee du bord « a verifier ». Sert au chemin web, ou le bord est un `background` clipe
 * et non un sprite, et a la legende de la carte, qui doit montrer la denture reelle plutot qu'une
 * approximation qui divergerait au premier reglage. Public pour cette seconde raison : c'est le
 * seul membre de ce fichier qu'un module au-dessus a besoin de nommer.
 */
val NotchedMarkerShape: Shape =
    GenericShape { size, _ ->
        val radius = size.minDimension / 2f
        val points =
            notchedRingPoints(
                outerRadius = radius,
                innerRadius = radius / MARKER_RIM_SCALE,
                center = radius,
            )
        moveTo(points[0], points[1])
        for (i in 2 until points.size step 2) {
            lineTo(points[i], points[i + 1])
        }
        close()
    }

/**
 * Painter du disque, d'identite stable : un painter recree a chaque recomposition ferait
 * re-enregistrer le sprite au style de MapLibre a chaque frame de camera (ADR 0009).
 */
@Composable
internal fun rememberMarkerDiscPainter(): Painter = rememberVectorPainter(MarkerDisc)

/** Pendant crenele de [rememberMarkerDiscPainter], meme exigence d'identite stable. */
@Composable
internal fun rememberMarkerDiscNotchedPainter(): Painter = rememberVectorPainter(MarkerDiscNotched)

/**
 * Cote de l'icone composite du chemin SDK : la plaque et son debord de bord, d'un seul tenant.
 */
internal val COMPOSITE_MARKER_SIZE: Dp = MARKER_BASE_SIZE * MARKER_RIM_SCALE

/**
 * Cote de l'icone composite du marqueur selectionne. Second raster plutot qu'un `iconSize` sur
 * celui du repos : MapLibre echantillonne l'atlas en bilineaire sans mipmap, un bitmap etire de
 * 35 % perd son trait (bords sur 3 a 4 px, glyphe sans pixel pur, mesure sur le web). Dessine a
 * la taille ou il s'affiche, il reste sur la grille de pixels.
 */
internal val SELECTED_COMPOSITE_MARKER_SIZE: Dp = COMPOSITE_MARKER_SIZE * SELECTED_MARKER_SCALE

/**
 * Cles de style dont la version agrandie doit etre enregistree au style : celles des marqueurs
 * selectionnes seulement, en pratique une. Les autres combinaisons gardent une seule image.
 */
internal fun selectedStyleKeys(markers: List<MapMarker>): Set<String> =
    markers.filter { it.selected }.mapTo(mutableSetOf(), ::markerStyleKey)

/** Part de la plaque occupee par le glyphe (20 dp sur une plaque de 36 dp). */
internal const val GLYPH_PLAQUE_FRACTION = 20f / 36f

/**
 * Cle d'apparence complete d'un marqueur (#269) : tout ce qui change l'icone composite, et rien
 * d'autre — la selection choisit un second raster de meme cle (voir [selectedStyleKeys]),
 * l'attenuation passe par `iconOpacity`. Serialisee sur
 * chaque feature GeoJSON et utilisee comme cle de l'expression `switch` cote couche : les deux
 * doivent rester le meme calcul.
 */
internal fun markerStyleKey(marker: MapMarker): String =
    "${marker.tint.toCssHex()}|${marker.soon}|${marker.flagged}|${marker.iconKey.orEmpty()}"

/**
 * Icone complete d'un marqueur — bord, plaque et glyphe dessines d'un bloc (#269).
 *
 * Trois couches de symboles rendaient la carte par etage (tous les bords, puis toutes les
 * plaques, puis tous les glyphes) : deux marqueurs qui se chevauchent laissaient la plaque de
 * l'un s'intercaler entre la plaque et le glyphe de l'autre. Une image par combinaison visible
 * rend chaque marqueur solidaire, au prix du SDF : le pipeline SDF ne rend qu'une couleur par
 * image, les teintes sont donc cuites dans le raster.
 */
internal class CompositeMarkerPainter(
    /** Bord crenele (« a verifier »), ou null pour le bord plein. */
    private val notchedRim: Painter?,
    private val rimColor: Color,
    private val plaqueColor: Color,
    private val glyph: Painter?,
    private val glyphColor: Color,
    /** Rapport du diametre total a celui de la plaque : le debord du bord. */
    private val rimScale: Float = MARKER_RIM_SCALE,
    /** Part de la plaque occupee par le glyphe. */
    private val glyphFraction: Float = GLYPH_PLAQUE_FRACTION,
) : Painter() {
    override val intrinsicSize: Size get() = Size.Unspecified

    /**
     * Bord plein et plaque en `drawCircle`, pas en `VectorPainter` : rasterise a une taille non
     * entiere (le marqueur selectionne, 139,8 px a 390 dpi), le cache bitmap du peintre vectoriel
     * laissait un trait d'un pixel a droite et sous la plaque, visible sur le bord blanc (#650).
     */
    override fun DrawScope.onDraw() {
        val full = size.minDimension
        val center = Offset(full / 2f, full / 2f)
        if (notchedRim != null) {
            with(notchedRim) { draw(Size(full, full), colorFilter = ColorFilter.tint(rimColor)) }
        } else {
            drawCircle(rimColor, radius = full / 2f, center = center)
        }
        val plaqueSide = full / rimScale
        drawCircle(plaqueColor, radius = plaqueSide / 2f, center = center)
        if (glyph != null) {
            val side = plaqueSide * glyphFraction
            val inset = (full - side) / 2f
            translate(inset, inset) {
                with(glyph) { draw(Size(side, side), colorFilter = ColorFilter.tint(glyphColor)) }
            }
        }
    }
}
